import { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { FETCH_MERGE_CHECKS_RETRY } from 'src/redux/pull-request/actions';
import {
  getIsAsyncMergeInProgress,
  getIsCurrentPullRequestOpen,
  getIsMergeChecklistLoading,
} from 'src/redux/pull-request/selectors';
import { backOffPoller } from 'src/redux/pull-request/utils/polling-delay-timer';

import { useMergeChecks } from './provider';
import { loadForgeMergeChecksResultsResource } from './resources';
import { isCustomCheckCompleted } from './utils';

const DEFAULT_INTERVAL = 5000;

/**
 * A hook which returns the merge check results and updates as it polls for more up-to-date results at the supplied
 * interval. Even if there are multiple components calling the same hook, it is guaranteed to only call the API once at
 * a time at a frequency never exceeding the lowest interval supplied to the hook.
 */
export const usePollingResults = (interval: number = DEFAULT_INTERVAL) => {
  const { data, refresh, error, loading } = useResource(
    loadForgeMergeChecksResultsResource
  );

  // set up the `backOffPoller` as a ref so it does not reset upon every component rerender
  const pollingCalculator = useRef(backOffPoller(interval));

  useEffect(() => {
    // clean up the `setInterval` created inside `backOffPoller`
    const currentPollingCalculator = pollingCalculator.current;
    return () => currentPollingCalculator.cleanup();
  }, []);

  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  useEffect(() => {
    // helper function which is used to calculate the delay until next polling which gradually increases when there are
    // no UI interactions
    const delay = pollingCalculator.current.calculateDelay();

    // this `useEffect` runs upon every data refresh as per the dependency array (`data` will update upon every call to
    // `refresh()`) which sets up the next polling at the calculated delay
    const timeoutId = setTimeout(() => {
      // If the PR is closed and all checks are complete, we don't need to poll anymore
      if (!isOpenPr && data && data.results.every(isCustomCheckCompleted)) {
        return;
      }
      // If the next poll is in the future, don't poll yet
      if (data && data.lastPolled + delay > Date.now()) {
        return;
      }
      refresh();
    }, delay);

    return () => clearTimeout(timeoutId);
    // Need to include `error` in the dependencies so that this gets re-run
    // when the resource is erroring. Otherwise, since `data` is always null,
    // the effect is never re-run since nothing changes.
  }, [interval, error, data, refresh, pollingCalculator, isOpenPr]);

  const isAsyncMergeInProgress = useSelector(getIsAsyncMergeInProgress);
  const prevIsAsyncMergeInProgress = useRef(isAsyncMergeInProgress);
  useEffect(() => {
    if (!prevIsAsyncMergeInProgress.current && isAsyncMergeInProgress) {
      // Force refresh of merge checks results upon completion of async merge API call. This will get triggered even
      // if the API call ends in an error which doesn't matter given that this endpoint gets polled every 5 seconds.
      refresh();
    }

    prevIsAsyncMergeInProgress.current = isAsyncMergeInProgress;
  }, [isAsyncMergeInProgress, refresh]);

  return {
    data,
    error,
    loading,
    refresh,
  };
};

type MergeChecksRetryHook = {
  isMergeChecksFetchRetrying: boolean;
  retryMergeChecksFetch: () => void;
};

export const useMergeChecksFetchRetry = (): MergeChecksRetryHook => {
  const [{ isMergeChecksFetchRetrying }, { setIsMergeChecksFetchRetrying }] =
    useMergeChecks();
  const { loading: customChecksLoading, refresh: refreshCustomChecks } =
    usePollingResults();

  const classicChecksLoading = useSelector(getIsMergeChecklistLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !customChecksLoading &&
      !classicChecksLoading &&
      isMergeChecksFetchRetrying
    ) {
      setIsMergeChecksFetchRetrying(false);
    }
  }, [
    classicChecksLoading,
    customChecksLoading,
    isMergeChecksFetchRetrying,
    setIsMergeChecksFetchRetrying,
  ]);

  const retry = useCallback(() => {
    setIsMergeChecksFetchRetrying(true);
    refreshCustomChecks();
    dispatch({ type: FETCH_MERGE_CHECKS_RETRY });
  }, [dispatch, refreshCustomChecks, setIsMergeChecksFetchRetrying]);

  return {
    isMergeChecksFetchRetrying,
    retryMergeChecksFetch: retry,
  };
};
